'use client'

import { Button } from '@/components/ui/button'
import {
	Card,
	CardContent,
	CardDescription,
	CardFooter,
	CardHeader,
	CardTitle,
} from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { brandingConfig } from '@/config/branding'
import { ROUTES } from '@/config/routes'
import { zodResolver } from '@hookform/resolvers/zod'
import { Loader2 } from 'lucide-react'
import Link from 'next/link'
import { useForm } from 'react-hook-form'
import { useAuth } from '../hooks/use-auth'
import { loginSchema, type LoginFormData } from '../schemas'

/**
 * Login form component.
 * Uses React Hook Form + Zod for validation, Supabase for auth.
 */
export function LoginForm() {
	const { login, isLoading, error, clearError } = useAuth()

	const {
		register,
		handleSubmit,
		formState: { errors },
	} = useForm({
		resolver: zodResolver(loginSchema),
		defaultValues: {
			email: '',
			password: '',
			keepSession: false,
		} satisfies LoginFormData,
	})

	const onSubmit = async (data: LoginFormData) => {
		clearError()
		await login(data)
	}

	return (
		<Card className="mx-auto w-full max-w-md">
			<CardHeader className="text-center">
				<CardTitle className="text-2xl font-bold">Iniciar Sesión</CardTitle>
				<CardDescription>Bienvenido a {brandingConfig.appName}</CardDescription>
			</CardHeader>

			<form onSubmit={handleSubmit(onSubmit)}>
				<CardContent className="space-y-4">
					{error && (
						<div className="text-destructive bg-destructive/10 rounded-md p-3 text-sm">{error}</div>
					)}

					<div className="space-y-2">
						<Label htmlFor="email">Correo electrónico</Label>
						<Input
							id="email"
							type="email"
							placeholder="tu@correo.com"
							autoComplete="email"
							disabled={isLoading}
							{...register('email')}
						/>
						{errors.email && <p className="text-destructive text-sm">{errors.email.message}</p>}
					</div>

					<div className="space-y-2">
						<Label htmlFor="password">Contraseña</Label>
						<Input
							id="password"
							type="password"
							placeholder="••••••••"
							autoComplete="current-password"
							disabled={isLoading}
							{...register('password')}
						/>
						{errors.password && (
							<p className="text-destructive text-sm">{errors.password.message}</p>
						)}
					</div>

					<div className="flex items-center justify-between">
						<div className="flex items-center space-x-2">
							<Checkbox id="keepSession" {...register('keepSession')} />
							<Label htmlFor="keepSession" className="text-sm">
								Mantener sesión
							</Label>
						</div>
						<Link href={ROUTES.FORGOT_PASSWORD} className="text-primary text-sm hover:underline">
							¿Olvidaste tu contraseña?
						</Link>
					</div>
				</CardContent>

				<CardFooter className="flex flex-col gap-4">
					<Button type="submit" className="w-full" disabled={isLoading}>
						{isLoading ? (
							<>
								<Loader2 className="mr-2 h-4 w-4 animate-spin" />
								Iniciando sesión...
							</>
						) : (
							'Iniciar Sesión'
						)}
					</Button>

					<p className="text-muted-foreground text-center text-sm">
						¿No tienes cuenta?{' '}
						<Link href={ROUTES.REGISTER} className="text-primary font-medium hover:underline">
							Regístrate
						</Link>
					</p>
				</CardFooter>
			</form>
		</Card>
	)
}
